import { Injectable, Inject } from '@angular/core';
import { APP_CONFIG } from '../app-config.constants';
import { IAppConfig } from '../app-config.interface';
import { HttpClient } from '@angular/common/http';
import { ObjectResponse } from '../classes/object-response';
import { environment } from '../../environments/environment-wrapper';
import { MortageRateCategory, MortgageRateSubCategory, MortgageRate } from '../classes/mortgage-rate';
import { PaymentsFrequency } from '../classes/enums';
import { map } from 'rxjs/operators';

// temporary static rates until XML is available
const StaticRates: MortgageRateSubCategory[] =
  [{
    name: 'Mortgage Rates',
    rates: [
      {
        term: '1 year',
        name: '1 Year Fixed Rate',
        rate: '2.79%'
      },
      {
        term: '2 year',
        name: '2 Year Fixed Rate',
        rate: '2.89%'
      },
      {
        term: '3 year',
        name: '3 Year Fixed Rate',
        rate: '3.49%'
      },
      {
        term: '5 year',
        name: '5 Year Fixed Rate',
        rate: '4.79%'
      },
      {
        term: '7 year',
        name: '7 Year Fixed Rate',
        rate: '5.60%'
      },
      {
        term: '3 year',
        name: '3 Year Variable Rate Flex',
        rate: '2.45%'
      },
      {
        term: '5 year',
        name: '5 Year Variable Rate Flex',
        rate: '2.45%'
      },
    ]
  }];

@Injectable()
export class MortgageService {
  private _rates: MortageRateCategory[];

  constructor(@Inject(APP_CONFIG) private appConfig: IAppConfig, private http: HttpClient) {
    // Set up static rates
    const rate = new MortageRateCategory();
    rate.rateCategory = 'Mortgage Rates';
    rate.subCategories = StaticRates;
    this._rates = [rate];
  }

  async getRates() {
    if (this._rates) return new Promise((resolve) => resolve(this._rates));
    // TODO: Errors
    return await this.http.get<ObjectResponse<any>>(environment.baseServerUrl + this.appConfig.urls.mortgage.base, { withCredentials: false })
      .pipe(map(res => {
        this._rates = res.result.map((r) => this._convertToRateCategory(r));
        return this._rates;
      }))
      .toPromise();
  }
 
  private _convertToRate(tmpRate): MortgageRate {
    const rate: MortgageRate = new MortgageRate();
    rate.term = tmpRate['@term'];
    rate.name = tmpRate.Name['#cdata-section'];
    rate.rate = tmpRate.Value['#cdata-section'];
    return rate;
  }

  // TODO: is there a way we can convert everything to array
  private _convertToRateSubCategory(tmpRateSub): MortgageRateSubCategory {
    const subCat: MortgageRateSubCategory = new MortgageRateSubCategory();
    subCat.name = tmpRateSub['@ID'];
    // In some cases where there is only one rate, its just a single object, not an array
    if (tmpRateSub.Rate.length) subCat.rates = tmpRateSub.Rate.filter(tmpR => tmpR.Value['#cdata-section'] !== '&nbsp;').map((tmpR) => this._convertToRate(tmpR));
    else subCat.rates = [this._convertToRate(tmpRateSub.Rate)];
    return subCat;
  }

  private _convertToRateCategory(tmpCat): MortageRateCategory {
    const rc: MortageRateCategory = new MortageRateCategory();
    const tmpRateCategory = tmpCat.RateCategory;
    rc.rateCategory = tmpRateCategory['@ID'];
    rc.subCategories = tmpRateCategory.RateSubCategory.map((tmpS) => this._convertToRateSubCategory(tmpS));
    return rc;
  }

  private _getPaymentsPerMonth(frequency: PaymentsFrequency): number {
    switch (frequency) {
      // TODO: CONSTANTS WUT
      case PaymentsFrequency.BiWeekly:
        return 12 / 26;
      case PaymentsFrequency.RapidBiWeekly:
        return 1 / 2;
      case PaymentsFrequency.Weekly:
        return 12 / 52;
      case PaymentsFrequency.RapidWeekly:
        return 1 / 4;
      default:
        return 1;
    }
  }

  private _getEffectiveAnnualInterestRate(decimalInterestRate: number): number {
    const compoundFrequencyPerYear = 2;
    return (1 + decimalInterestRate / compoundFrequencyPerYear) ** compoundFrequencyPerYear - 1;
  }

  private _getMonthlyInterestRate(annualRate: number): number {
    return (1 + annualRate) ** (1 / 12) - 1;
  }

/**
 * All payment frequencies are based on the monthly rate.
 * @param mortgageAmount the principal amount of the loan (we don't account for down payments so this ends up being the listing price)
 * @param decimalInterestRate the interest rate provided by the bank
 * @param amortizationPeriod the number of years setup to payoff the loan
 * @returns a monthly payment value
 */
  private _getMonthlyMortgagePaymentRate(
    mortgageAmount: number,
    decimalInterestRate: number,
    amortizationPeriod: number
  ): number {
    const interestPerMonth = this._getMonthlyInterestRate(this._getEffectiveAnnualInterestRate(decimalInterestRate));
    const totalMonths = amortizationPeriod * 12;
    return (mortgageAmount * interestPerMonth) / (1 - (1 + interestPerMonth) ** (-1 * totalMonths));
  };

  private _getCost(mortgageAmount: number, paymentsPerMonth: number,
    decimalInterestRate: number, amortizationPeriod: number): number {
    
    return this._getMonthlyMortgagePaymentRate(mortgageAmount, decimalInterestRate, amortizationPeriod) * paymentsPerMonth;
  };

  public calculateMortgagePayment(mortgageAmount: number, decimalInterestRate: number, paymentsPerMonthEnumeration: number, amortizationPeriod: number): number {
    const paymentsPerMonth = this._getPaymentsPerMonth(paymentsPerMonthEnumeration);

    const cost: number = this._getCost(mortgageAmount, paymentsPerMonth, decimalInterestRate, amortizationPeriod);

    return isNaN(cost) ? 0 : cost;  
  }
}


